// src/components/ecommerce/ShippingStep.jsx
import React from 'react';
import { MapPin, Truck, Clock } from 'lucide-react';

export const ShippingStep = ({ orderData, onUpdate, storeSettings }) => {
  const handleInputChange = (field, value) => {
    onUpdate('shipping', { [field]: value });
  };

  const handleDeliveryZoneChange = (zoneName) => {
    const zone = storeSettings.shipping?.deliveryZones?.find(z => z.name === zoneName);
    handleInputChange('deliveryZone', zone);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Delivery Information</h3>
        <p className="text-gray-600">Where should we deliver your order?</p>
      </div>

      {/* Delivery Address */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Street Address *
          </label>
          <input
            type="text"
            value={orderData.shipping.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="123 Main Street"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              value={orderData.shipping.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Durban"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postal Code
            </label>
            <input
              type="text"
              value={orderData.shipping.postalCode}
              onChange={(e) => handleInputChange('postalCode', e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="4001"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Province
          </label>
          <select
            value={orderData.shipping.province}
            onChange={(e) => handleInputChange('province', e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Province</option>
            <option value="western-cape">Western Cape</option>
            <option value="eastern-cape">Eastern Cape</option>
            <option value="northern-cape">Northern Cape</option>
            <option value="free-state">Free State</option>
            <option value="kwazulu-natal">KwaZulu-Natal</option>
            <option value="north-west">North West</option>
            <option value="gauteng">Gauteng</option>
            <option value="mpumalanga">Mpumalanga</option>
            <option value="limpopo">Limpopo</option>
          </select>
        </div>
      </div>

      {/* Delivery Zone Selection */}
      {storeSettings.shipping?.deliveryZones?.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Delivery Zone *
          </label>
          <div className="space-y-3">
            {storeSettings.shipping.deliveryZones.map((zone) => (
              <div
                key={zone.name}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  orderData.shipping.deliveryZone?.name === zone.name
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleDeliveryZoneChange(zone.name)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      checked={orderData.shipping.deliveryZone?.name === zone.name}
                      onChange={() => handleDeliveryZoneChange(zone.name)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">{zone.name}</h4>
                      <p className="text-sm text-gray-600">{zone.areas}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">R{zone.fee}</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {zone.deliveryTime}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delivery Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Delivery Notes (Optional)
        </label>
        <textarea
          value={orderData.shipping.deliveryNotes}
          onChange={(e) => handleInputChange('deliveryNotes', e.target.value)}
          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows="3"
          placeholder="Any special delivery instructions..."
        />
      </div>

      {/* Store Pickup Option */}
      {storeSettings.shipping?.allowPickup && (
        <div className="border-t border-gray-200 pt-4">
          <div
            className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              orderData.shipping.deliveryZone?.name === 'pickup'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleDeliveryZoneChange('pickup')}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                checked={orderData.shipping.deliveryZone?.name === 'pickup'}
                onChange={() => handleDeliveryZoneChange('pickup')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <h4 className="font-medium text-gray-900">Store Pickup</h4>
                <p className="text-sm text-gray-600">Collect your order from our store</p>
                <p className="text-sm font-semibold text-green-600">FREE</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};